import { defineStore } from 'pinia'
import type {
  TAirportType,
  TFlightType,
  TTripType,
  IQueryParams,
  ILocationQueryParams,
  TFormQueryParams
} from '@/components/fsf/types'
import { ISO_DATE_FORMAT, ISO_YEAR_AND_MONTH_FORMAT, DATE_NO_SEPARATOR_FORMAT } from '@/utils/constants/date'
import { OUTBOUND, RETURN } from '@/utils/constants/fsf'
import { usePaxStore } from '@/components/fsf/pax/store'
import { useVoucherStore } from '@/components/fsf/voucher/store'
import { useLocaleStore } from '@/stores/Locale.store'
import { useGlobalSettingsStore } from '@/stores/GlobalSettings.store'
import useFSFDataLayer from '@/composables/data-layers/useFSFDataLayer'
import { customSAPEvent } from '@/utils/sap'
import { COOKIE_OPTIONS } from '@/utils/constants/cookies'

export const useFsfStore = defineStore('Fsf', () => {
  const dayjs = useDayjs()
  const { pushFSFCalendarSearchDataLayer } = useFSFDataLayer()
  const { isDesktopLarge } = useBTBreakpoints()

  const origin = ref<string>()
  const destin = ref<string>()

  const originType = ref<TAirportType>()
  const destinType = ref<TAirportType>()

  const sref = ref<string>()
  const upsltab = ref<string>()

  const isSafari = ref(false)

  const currentFlightType = ref<TFlightType>(OUTBOUND)
  const currentFlightMode = ref<TTripType>(RETURN)
  const currentDateRange = computed(() => {
    const startDate = dayjs()
    const endDate = startDate.endOf('month').add(12, 'months')

    return {
      startDate: startDate.format(ISO_DATE_FORMAT),
      endDate: endDate.format(ISO_DATE_FORMAT)
    }
  })

  const selectedStartDate = ref<string | undefined>()
  const selectedEndDate = ref<string | undefined>()
  const selectedMonth = ref(dayjs().format(ISO_YEAR_AND_MONTH_FORMAT))

  const isOutboundFlightType = computed(() => currentFlightType.value === OUTBOUND)
  const isRoundTrip = computed(() => currentFlightMode.value === RETURN)

  const errorRef = ref<string>()
  const errorCodes = ref<string[]>([])
  const errorTexts = ref<string[]>([])

  const isABVariant = computed(() => false)
  const showPrices = ref(!isABVariant.value)

  const isBusinessClass = computed(() => upsltab.value === 'BUS')

  const setBusinessClass = (value: boolean | undefined) => {
    if (value === true) {
      upsltab.value = 'BUS'
      showPrices.value = false
      return
    }
    if (upsltab.value === 'BUS') {
      upsltab.value = undefined
      showPrices.value = true
    }
  }

  watch(
    () => currentFlightType.value,
    (value) => {
      let selected = '0'
      if (value === OUTBOUND) {
        selected = selectedStartDate.value
          ? dayjs(selectedStartDate.value).format(ISO_YEAR_AND_MONTH_FORMAT)
          : dayjs().format(ISO_YEAR_AND_MONTH_FORMAT)
      } else {
        selected = dayjs(selectedStartDate.value).format(ISO_YEAR_AND_MONTH_FORMAT)
      }

      selectedMonth.value = selected
    }
  )

  const paxStore = usePaxStore()
  const voucherStore = useVoucherStore()

  const getVouchers = computed(() => voucherStore.vouchers.map((v) => v.inputValue).join(','))

  const queryParams = computed<IQueryParams>(() => {
    const data = {
      originCode: origin.value,
      destinCode: destin.value,
      tripType: currentFlightMode.value,
      departure: selectedStartDate.value,
      return: selectedEndDate.value,
      numAdt: paxStore.selected.adults,
      numChd: paxStore.selected.children,
      numInf: paxStore.selected.infants,
      numYth: paxStore.selected.youngAdults,
      evoucher: getVouchers.value,
      originType: originType.value,
      destinType: destinType.value,
      selectedMonth: undefined as number | undefined,
      sref: sref.value,
      upsltab: upsltab.value
    }

    if (selectedMonth.value) {
      const monthDiff = dayjs(selectedMonth.value, ISO_YEAR_AND_MONTH_FORMAT)
        .startOf('month')
        .diff(dayjs().startOf('month'), 'months')

      data.selectedMonth = monthDiff
    }

    return data
  })
  const routeBaseName = useRouteBaseName()
  const route = useRoute()
  const isBookFlightRoute = computed(() => routeBaseName(route) === 'book-flight')
  let lastSearchEventKey: string | undefined

  const sendSearchEvent = () => {
    if (!origin.value || !destin.value) return

    const searchEvent = {
      orig: origin.value,
      dest: destin.value,
      adt: String(paxStore.selected.adults + paxStore.selected.youngAdults),
      chd: String(paxStore.selected.children),
      inf: String(paxStore.selected.infants),
      trip_type: currentFlightMode.value === RETURN ? 'rt' : 'ow'
    }
    const searchEventKey = JSON.stringify(searchEvent)
    if (searchEventKey === lastSearchEventKey) return

    lastSearchEventKey = searchEventKey
    customSAPEvent('search', searchEvent).then((sent) => {
      if (!sent && lastSearchEventKey === searchEventKey) lastSearchEventKey = undefined
    })
  }

  watch(
    () => queryParams.value,
    async (params, prev) => {
      if (!isBookFlightRoute.value) {
        return
      }

      if (JSON.stringify(params) === JSON.stringify(prev)) return

      await useRouter().replace({
        query: {
          ...useRoute().query, // keep existing query params (e.g. UTM, ref codes)
          ...params // add selected and validated parameters
        }
      })

      sendSearchEvent()
    },
    {
      flush: 'post'
    }
  )

  const $reset = () => {
    selectedStartDate.value = undefined
    selectedEndDate.value = undefined
    currentFlightType.value = OUTBOUND
    selectedMonth.value = dayjs().format(ISO_YEAR_AND_MONTH_FORMAT)
  }

  const localeStore = useLocaleStore()

  const language = computed(() => localeStore.selectedLanguage)
  const country = computed(() => localeStore.selectedCountry)

  const locationQueryParams = computed<ILocationQueryParams>(() => ({
    p: 'bti',
    l: language.value,
    pos: country.value
  }))

  const formDataToString = (query: TFormQueryParams) => {
    return new URLSearchParams(
      Object.fromEntries(Object.entries(query).map(([key, value]) => [key, String(value ?? '')]))
    ).toString()
  }

  const globalSettingsStore = useGlobalSettingsStore()
  const { hideFsfPrices, openBookingTab } = storeToRefs(globalSettingsStore)

  const submitGroupForm = () => {
    const fullUrl = `${globalSettingsStore.groupBookingLink?.href}?${formDataToString({
      ...queryParams.value
    })}`

    window.location.href = fullUrl
  }

  const runtimeConfig = useRuntimeConfig()
  const E2URL = runtimeConfig.public.ibeUrl
  const bookingAidFsf = runtimeConfig.public.bookingAidFsf
  const bookingLabelFsf = runtimeConfig.public.bookingLabelFsf

  const getCalendarSearchData = () => ({
    origin_airport_code: origin.value,
    destination_airport_code: destin.value,
    trip_type: isRoundTrip.value ? 'RT' : 'OW',
    departure_date: dayjs(selectedStartDate.value).format(DATE_NO_SEPARATOR_FORMAT),
    return_date: isRoundTrip.value ? dayjs(selectedEndDate.value).format(DATE_NO_SEPARATOR_FORMAT) : undefined,
    noa: paxStore.selected.adults,
    noc: paxStore.selected.children + paxStore.selected.youngAdults,
    noi: paxStore.selected.infants,
    tripvoucher: getVouchers.value,
    sref: sref.value,
    upsltab: upsltab.value
  })

  onMounted(() => {
    const userAgent = navigator.userAgent
    isSafari.value = /Safari/.test(userAgent) && !/Chrome/.test(userAgent)
  })

  const openBookingLink = computed(() => {
    return openBookingTab.value && !isSafari.value && isDesktopLarge.value
  })

  const submitForm = async () => {
    pushFSFCalendarSearchDataLayer(getCalendarSearchData())

    if (paxStore.isGroupBooking) {
      submitGroupForm()

      return
    }

    const bookingCheckoutDate = isRoundTrip.value
      ? selectedEndDate.value
      : dayjs(selectedStartDate.value).add(1, 'day').format(ISO_DATE_FORMAT)

    const bookingLinkParams = new URLSearchParams({
      aid: String(bookingAidFsf),
      checkin: String(selectedStartDate.value),
      checkout: String(bookingCheckoutDate),
      iata_orr: '1',
      iata: String(destin.value),
      label: String(bookingLabelFsf),
      lang: String(language.value),
      group_adults: String(paxStore.selected.adults),
      group_children: String(paxStore.selected.children + paxStore.selected.youngAdults)
    })

    const bookingLink = `https://www.booking.com/searchresults.html?${bookingLinkParams.toString()}`

    const queryString = formDataToString({ ...queryParams.value, ...locationQueryParams.value })
    const fullUrl = `${E2URL}/${language.value}/fb/availability?${queryString}`

    const searchTarget = openBookingLink.value ? '_blank' : '_self'

    await navigateTo(fullUrl, {
      external: true,
      open: {
        target: searchTarget
      }
    })

    if (openBookingLink.value) {
      await navigateTo(bookingLink, { external: true })
    }
  }

  return {
    origin,
    destin,
    originType,
    destinType,
    currentFlightType,
    currentDateRange,
    currentFlightMode,
    selectedStartDate,
    selectedEndDate,
    selectedMonth,
    isOutboundFlightType,
    isRoundTrip,
    sref,
    upsltab,
    $reset,
    submitForm,
    errorCodes,
    errorTexts,
    errorRef,
    hideFsfPrices,
    showPrices,
    isABVariant,
    isBusinessClass,
    setBusinessClass
  }
})
